import { useCallback, useMemo } from 'react';

import { MultiOpenAccordion } from '@/components/accordion/accordion-multi-open';
import { AccordionWrapper } from '@/components/accordion/accordion-wrapper';
import { useFileTreeContext } from '@/pages/insight/folders/use-file-tree-context';
import { TreeEntry } from '@/pages/insight/folders/util/document-list-to-tree';

import { AllDocuments } from './all-documents';
import { Category } from './category';
import { CategoryContent } from './category-content';

/**
 * Clickable and collapsible header component for accordion that displays category info
 * and allows both clicking to navigate and expanding/collapsing
 */
const CategoryAccordionHeader = ({
  title,
  entry,
  onClick,
}: {
  title: string;
  entry: TreeEntry;
  onClick: (id: string) => void;
}) => {
  return (
    <Category
      title={title}
      entry={entry}
      mode="default"
      showDocumentCount={true}
      showToggle={false}
      onCustomClick={() => onClick(entry.id)}
      className="pr-8"
    />
  );
};

/**
 * Clickable category component for the uncategorized folder
 * Behaves like a regular category instead of an accordion header
 */
const ClickableCategory = ({ title, entry }: { title: string; entry: TreeEntry }) => {
  return <Category title={title} entry={entry} mode="clickable" showDocumentCount={true} />;
};

/**
 * CategoryAccordion component that renders a collapsible list of document categories
 *
 * This component displays an accordion interface where each category can be expanded
 * to show its contents. It includes an "All Documents" section at the top and
 * renders each category as a separate accordion item with proper headers and content.
 * The uncategorized folder is rendered as a clickable category instead of a collapsible accordion.
 *
 * @param categories - Array of category entries to display
 * @returns the O&M category accordion
 */
export const CategoryAccordion = (categories: [string, TreeEntry][]) => {
  const { setActiveFolderId } = useFileTreeContext();
  const { setPage } = useFileTreeContext();

  const handleCategoryClick = useCallback(
    (id: string) => {
      setActiveFolderId(id);
      setPage(1);
    },
    [setActiveFolderId, setPage],
  );

  // Separate uncategorized folder from other categories
  const uncategorizedFolder = useMemo(() => {
    if (!categories) return null;
    return Object.values(categories).find(([, entry]) => entry.id === 'UNCATEGORIZED');
  }, [categories]);

  const regularCategories = useMemo(() => {
    if (!categories) return [];
    return Object.values(categories).filter(([, entry]) => entry.id !== 'UNCATEGORIZED');
  }, [categories]);

  const regularCategoryHeaders = useMemo(() => {
    return regularCategories.map(([, entry]) => ({
      id: entry.id,
      header: () => (
        <CategoryAccordionHeader
          title={entry.toDisplayString()}
          entry={entry}
          onClick={handleCategoryClick}
        />
      ),
      entry,
    }));
  }, [regularCategories, handleCategoryClick]);

  return (
    <AccordionWrapper>
      <AllDocuments />
      {/* Render other categories as collapsible accordions */}
      {regularCategoryHeaders.map(({ id, header, entry }, index) => {
        return (
          <MultiOpenAccordion
            key={id}
            index={index}
            classNames="[&&]:px-0 [&&]:py-0"
            disabled={false}
            header={header}
          >
            <CategoryContent onClick={handleCategoryClick} content={entry} />
          </MultiOpenAccordion>
        );
      })}
      {/* Render uncategorized folder as clickable category */}
      {uncategorizedFolder && (
        <ClickableCategory
          title={uncategorizedFolder[1].toDisplayString()}
          entry={uncategorizedFolder[1]}
        />
      )}
    </AccordionWrapper>
  );
};
